/**
 * AVA-660: pure math and decisions for the 360 mini map (PanoramaMiniMap.vue).
 * No Vue, store or THREE imports, so `npm run test:unit` (node --test) loads it directly.
 * Spec: .claude/features/AVA-660_360_MINIMAP.md.
 */

import { MINI_MAP } from './miniMapConfig.js';
import { CLICK_MOVE_TOLERANCE_PX } from '../../../constants/pointer.js';

/**
 * A numeric design token (CSS custom property, e.g. '16px' -> 16) as the page resolves it.
 * @param {string} name - e.g. MINI_MAP.tokens.iconSize
 * @param {Element} [el] - where to resolve it (theme overrides); the document root by default
 * @returns {number}
 */
export function readCssNumber(name, el = document.documentElement) {
  return parseFloat(getComputedStyle(el).getPropertyValue(name));
}

/**
 * Radar scale: model units per px so a `diameter` fits the canvas's short side, minus the padding.
 * @param {number} diameter
 * @param {number} width
 * @param {number} height
 * @param {number} paddingPx - MINI_MAP.tokens.padding
 * @returns {number}
 */
export function radarUnitsPerPx(diameter, width, height, paddingPx) {
  return diameter / (Math.min(width, height) - 2 * paddingPx);
}

/**
 * North-up Radar transform, `centre` in the middle of a width × height canvas.
 * @param {{ x: number, y: number }} centre
 * @param {number} unitsPerPx - radarUnitsPerPx()
 * @param {number} width
 * @param {number} height
 * @returns {(point: { x: number, y: number }) => [number, number]}
 */
export function radarViewTransform(centre, unitsPerPx, width, height) {
  return (point) => [width / 2 + (point.x - centre.x) / unitsPerPx, height / 2 - (point.y - centre.y) / unitsPerPx];
}

/**
 * The Radar's diameter after one wheel step, kept within the default-zoom limits.
 * @param {number} diameter
 * @param {boolean} zoomIn
 * @returns {number}
 */
export function zoomedDiameter(diameter, zoomIn) {
  const { wheelFactor } = MINI_MAP;
  const { minM, maxM } = MINI_MAP.span;
  const next = zoomIn ? diameter / wheelFactor : diameter * wheelFactor;
  return Math.min(maxM, Math.max(minM, next));
}

/**
 * The scan-index entries on one level (`UNASSIGNED_LEVEL` included).
 * @param {{ levelId: string }[]} entries - state.scanIndex.entries
 * @param {string} levelId
 * @returns {object[]}
 */
export function scansOnLevel(entries, levelId) {
  return (entries || []).filter((entry) => entry.levelId === levelId);
}

/**
 * @param {{ x: number, y: number, item: * }[]} drawn
 * @param {number} x
 * @param {number} y
 * @param {number} [radiusPx]
 * @returns {*} the item nearest to (x, y) within the radius, or null
 */
export function hitTest(drawn, x, y, radiusPx) {
  let best = null;
  let bestDistance = radiusPx * radiusPx;
  for (const point of drawn || []) {
    const distance = (point.x - x) ** 2 + (point.y - y) ** 2;
    if (distance <= bestDistance) {
      bestDistance = distance;
      best = point.item;
    }
  }
  return best;
}

/**
 * How long `metres` (east of `worldPoint`) is on the 2D plan.
 * @param {(p: { x: number, y: number, z: number }) => { x: number, y: number }} to2d - level mapping `_3dTo2d` (world metres in)
 * @param {{ x: number, y: number, z: number }} worldPoint
 * @param {number} metres
 * @returns {number}
 */
export function planSpan(to2d, worldPoint, metres) {
  const a = to2d(worldPoint);
  const b = to2d({ x: worldPoint.x + metres, y: worldPoint.y, z: worldPoint.z });
  return Math.hypot(b.x - a.x, b.y - a.y);
}

/**
 * @param {{ x: number, y: number }} start
 * @param {{ x: number, y: number }} current
 * @param {number} [threshold]
 * @returns {boolean} true once the pointer moved far enough to be a pan
 */
export function isUserPan(start, current, threshold = CLICK_MOVE_TOLERANCE_PX) {
  return Math.abs(current.x - start.x) + Math.abs(current.y - start.y) > threshold;
}

/**
 * Metres across the default view: twice the distance to the `nearestScans`-th nearest scan (the
 * farthest one when there are fewer), kept within [minM, maxM]; maxM when there are none.
 * @param {number[]} distancesM - distances (m) from the current scan to the others on its level
 * @param {{ nearestScans: number, minM: number, maxM: number }} [limits]
 * @returns {number}
 */
export function spanFromNeighbours(distancesM, limits = MINI_MAP.span) {
  if (!distancesM || distancesM.length === 0) return limits.maxM;
  const sorted = [...distancesM].sort((a, b) => a - b);
  const reach = sorted[Math.min(limits.nearestScans, sorted.length) - 1];
  return Math.min(limits.maxM, Math.max(limits.minM, 2 * reach));
}

/**
 * How strongly to draw a scan (MINI_MAP.opacity): full within the open 360's hotspot range, fading
 * with distance beyond it, faint on another level.
 * @param {{ inRange: boolean, distance: number, range: number, sameLevel: boolean }} scan
 * @returns {number} opacity in [0, 1]
 */
export function scanOpacity({ inRange, distance, range, sameLevel }) {
  const o = MINI_MAP.opacity;
  if (!sameLevel) return o.otherLevel;
  if (inRange) return o.inRange;
  if (!(range > 0)) return o.outOfRangeFar;
  const t = Math.min(1, Math.max(0, (distance - range) / (range * (o.fadeEndRanges - 1))));
  return o.outOfRangeNear + (o.outOfRangeFar - o.outOfRangeNear) * t;
}

/**
 * What the mini map does with the shared 2D plan viewer next.
 * @param {{
 *   planPaneVisible: boolean,   a MainView 2D pane is on screen
 *   status: 'unmapped' | 'missing' | 'other-level' | 'ready',   viewer.planReadyFor(level)
 *   loading: boolean,   a loadPlanInto is in flight
 *   hostHoldsNode: boolean,   the map's plan host contains a plan node
 *   nodeIsCurrent: boolean,   that node is the store's current GET_PAGE_VIEW_2D
 * }} state
 * @returns {'return' | 'load' | 'borrow' | 'radar' | 'none'}
 */
export function nextPlanAction({ planPaneVisible, status, loading, hostHoldsNode, nodeIsCurrent }) {
  // A load in flight lands in the map; it is returned when this runs again on arrival.
  if (planPaneVisible) return hostHoldsNode ? 'return' : 'none';
  if (status === 'unmapped') return 'radar';
  if (status === 'missing' || status === 'other-level') return loading ? 'none' : 'load';
  return !hostHoldsNode || !nodeIsCurrent ? 'borrow' : 'none';
}
